import React, { useEffect, useState } from 'react';
import Layout from './Layout';
import { defaultAxiosInstance } from '../api/defaultInstance';
import { useUser } from '../context/UserContext';
import '../css/CampaignHistory.css';

function CampaignHistory() {
    const { userInfo } = useUser();
    // data-scope: D-14 — partner는 자기 데이터만 보는 표시·필터 범위다(기능 권한 판단 아님, 권한 시스템 범위 밖)
    const isPartnerScope = userInfo?.role === 'partner'; // data-scope: D-14
    const [isLoading, setIsLoading] = useState(false);
    const [list, setList] = useState([]);
    const [summary, setSummary] = useState({
        total_reward_10: 0,
        total_reward_5_root: 0,
        total_reward_5_parent: 0,
        total_reward_1: 0,
        total_cost: 0,
        total_inflow: 0
    });
    const [filter, setFilter] = useState({
        startDate: '',
        endDate: '',
        name: '',
        adProductMetaId: '',
        parentUserName: '',
        page: 1,
        limit: 20
    });
    const [totalCount, setTotalCount] = useState(0);
    const [adProductMetas, setAdProductMetas] = useState([]);

    // 상품 목록 조회
    const fetchAdProductMetas = async () => {
        try {
            const response = await defaultAxiosInstance.get('/advertisement/admin/products/meta/simple');
            if (response.data.status === 200) {
                setAdProductMetas(response.data.data || []);
            }
        } catch (error) {
            console.error('상품 목록 조회 오류:', error);
        }
    };

    // 데이터 조회
    const fetchData = async (currentFilter = filter) => {
        setIsLoading(true);
        try {
            const params = {
                page: currentFilter.page || 1,
                limit: currentFilter.limit || 20
            };

            if (currentFilter.startDate) params.startDate = currentFilter.startDate;
            if (currentFilter.endDate) params.endDate = currentFilter.endDate;
            if (currentFilter.name) params.name = currentFilter.name;
            if (currentFilter.adProductMetaId) params.adProductMetaId = currentFilter.adProductMetaId;
            if (currentFilter.parentUserName) params.parentUserName = currentFilter.parentUserName;

            const response = await defaultAxiosInstance.get('/advertisement/admin/campaign-history', {
                params
            });

            if (response.data.status === 200) {
                const data = response.data.data;
                setList(data.list || []);
                setTotalCount(data.totalCount || 0);
                setSummary(data.summary || {
                    total_reward_10: 0,
                    total_reward_5_root: 0,
                    total_reward_5_parent: 0,
                    total_reward_1: 0,
                    total_cost: 0,
                    total_inflow: 0
                });
            }
        } catch (error) {
            console.error('데이터 조회 오류:', error);
            alert('데이터 조회 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAdProductMetas();
        // 초기 날짜 설정 (이번 달 1일)
        const now = new Date();
        const firstDayOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        const today = new Date().toISOString().split('T')[0];
        const initialFilter = {
            startDate: firstDayOfMonth,
            endDate: today,
            name: '',
            adProductMetaId: '',
            parentUserName: '',
            page: 1,
            limit: 20
        };
        setFilter(initialFilter);
        // 초기 데이터 조회
        fetchData(initialFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);

    useEffect(() => {
        if (filter.startDate && filter.endDate) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.page]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilter(prev => ({ ...prev, [name]: value }));
    };

    const applyFilter = () => {
        const filterToApply = { ...filter, page: 1 };
        setFilter(prev => ({ ...prev, page: 1 }));
        fetchData(filterToApply);
    };

    const handlePrevPage = () => {
        const newPage = Math.max(1, filter.page - 1);
        const updatedFilter = { ...filter, page: newPage };
        setFilter(updatedFilter);
        fetchData(updatedFilter);
    };

    const handleNextPage = () => {
        const newPage = filter.page + 1;
        const updatedFilter = { ...filter, page: newPage };
        setFilter(updatedFilter);
        fetchData(updatedFilter);
    };

    const handlePageClick = (page) => {
        const updatedFilter = { ...filter, page };
        setFilter(updatedFilter);
        fetchData(updatedFilter);
    };

    const startIndex = (filter.page - 1) * filter.limit + 1;
    const endIndex = Math.min(filter.page * filter.limit, totalCount);
    const totalPages = Math.ceil(totalCount / filter.limit);

    // 적립일 계산 (종료일 + 1일)
    const getAccumulationDate = (endDate) => {
        if (!endDate) return '';
        const date = new Date(endDate);
        date.setDate(date.getDate() + 1);
        return date.toISOString().split('T')[0];
    };

    return (
        <Layout>
            <div className="campaign-history-container">
                {/* 요약 카드 */}
                <div className={`summary ${isPartnerScope ? 'partner-summary' : ''}`}>
                    <div className="summary-box">
                        10% 정산액<br />
                        <strong>{summary.total_reward_10?.toLocaleString() || 0}원</strong>
                    </div>
                    <div className="summary-box">
                        강사 5% 정산액<br />
                        <strong>{summary.total_reward_5_root?.toLocaleString() || 0}원</strong>
                    </div>
                    {!isPartnerScope && (
                        <div className="summary-box">
                            사용자 5% 정산액<br />
                            <strong>{summary.total_reward_5_parent?.toLocaleString() || 0}원</strong>
                        </div>
                    )}
                    <div className="summary-box">
                        {isPartnerScope ? '내가 쓴 적립액' : '1% 적립액'}<br />
                        <strong>{summary.total_reward_1?.toLocaleString() || 0}원</strong>
                    </div>
                </div>

                {/* 필터 섹션 */}
                <div className="filter-bar">
                    <div className={`filter-group ${isPartnerScope ? 'filter-group-span-4' : ''}`}>
                        <label>조회 기간 설정</label>
                        <div className="date-range">
                            <input
                                type="checkbox"
                                name="allPeriod"
                                id="allPeriod"
                                checked={!filter.startDate && !filter.endDate}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setFilter(prev => ({ ...prev, startDate: '', endDate: '' }));
                                    } else {
                                        const now = new Date();
                                        const firstDayOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
                                        const today = new Date().toISOString().split('T')[0];
                                        setFilter(prev => ({ ...prev, startDate: firstDayOfMonth, endDate: today }));
                                    }
                                }}
                            />
                            <label htmlFor="allPeriod">전체</label>
                            <input
                                type="date"
                                name="startDate"
                                value={filter.startDate}
                                onChange={handleFilterChange}
                                disabled={!filter.startDate && !filter.endDate}
                            />
                            <span>~</span>
                            <input
                                type="date"
                                name="endDate"
                                value={filter.endDate}
                                onChange={handleFilterChange}
                                disabled={!filter.startDate && !filter.endDate}
                            />
                        </div>
                    </div>
                    {!isPartnerScope && (
                        <div className="filter-group">
                            <label>이름</label>
                            <input
                                type="text"
                                name="name"
                                placeholder="이름 입력"
                                value={filter.name}
                                onChange={handleFilterChange}
                                onKeyDown={e => { if (e.key === 'Enter') applyFilter(); }}
                            />
                        </div>
                    )}
                    {!isPartnerScope && (
                        <div className="filter-group">
                            <label>상위 유저</label>
                            <input
                                type="text"
                                name="parentUserName"
                                placeholder="유저명"
                                value={filter.parentUserName}
                                onChange={handleFilterChange}
                                onKeyDown={e => { if (e.key === 'Enter') applyFilter(); }}
                            />
                        </div>
                    )}
                    
                    <div className="filter-group">
                        <label>상품명</label>
                        <select
                            name="adProductMetaId"
                            value={filter.adProductMetaId}
                            onChange={handleFilterChange}
                        >
                            <option value="">전체</option>
                            {adProductMetas.map(meta => (
                                <option key={meta.adProductMetaId} value={meta.adProductMetaId}>
                                    {meta.productMetaName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group aggregate-item">
                        <label>총 접수량</label>
                        <input type="text" value={totalCount} disabled />
                    </div>
                    <div className="filter-group aggregate-item">
                        <label>총 구매금액</label>
                        <input type="text" value={Math.ceil(summary.total_cost/1.1)?.toLocaleString() || 0} disabled />
                    </div>
                    <div className="filter-group query-button-group">
                        <button onClick={applyFilter} className="query-button">
                            조회
                        </button>
                    </div>
                </div>

                {/* 테이블 */}
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>캐치랭크 접수일</th>
                                <th>이름</th>
                                <th>적립일(종료일 +1일)</th>
                                <th>상품명</th>
                                <th>구매금액</th>
                                <th>10% 적립액</th>
                                <th>강사 5% 적립액</th>
                                {!isPartnerScope && <th>사용자 5% 적립액</th>}
                                <th>적립 대상</th>
                                {!isPartnerScope && <th>멤버십 1% 적립</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={isPartnerScope ? "8" : "9"} className="center-cell">로딩 중...</td>
                                </tr>
                            ) : list.length === 0 ? (
                                <tr>
                                    <td colSpan={isPartnerScope ? "8" : "9"} className="center-cell">데이터가 없습니다.</td>
                                </tr>
                            ) : (
                                list.map((item, idx) => (
                                    <tr key={item.ad_campaign_meta_id || idx}>
                                        <td>{item.created_at ? item.created_at.split('T')[0] : ''}</td>
                                        <td>{item.user_name || ''}</td>
                                        <td>{getAccumulationDate(item.end_date)}</td>
                                        <td>{item.product_meta_name || ''}</td>
                                        <td>{item.purchase_amount?.toLocaleString() || 0}원</td>
                                        <td>{item.reward_10?.toLocaleString() || 0}원</td>
                                        <td>{item.reward_5_root?.toLocaleString() || 0}원</td>
                                        {!isPartnerScope && <td>{item.reward_5_parent?.toLocaleString() || 0}원</td>}
                                        <td>{item.reward_target || ''}</td>
                                        {!isPartnerScope && <td>{item.reward_1?.toLocaleString() || 0}원</td>}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* 페이징 */}
                <div className="pagination-info">
                    <div>총 {totalCount}개 항목 중 {startIndex} - {endIndex}</div>
                    <div className="pagination">
                        <button onClick={handlePrevPage} disabled={filter.page === 1 || isLoading}>이전</button>
                        {totalPages <= 10 ? (
                            Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    className={filter.page === page ? "active" : ""}
                                    onClick={() => handlePageClick(page)}
                                    disabled={isLoading}
                                >
                                    {page}
                                </button>
                            ))
                        ) : (
                            <>
                                {filter.page > 1 && <button onClick={() => handlePageClick(1)} disabled={isLoading}>1</button>}
                                {filter.page > 2 && <span>...</span>}
                                {filter.page > 0 && <button className="active" disabled>{filter.page}</button>}
                                {filter.page < totalPages - 1 && <span>...</span>}
                                {filter.page < totalPages && <button onClick={() => handlePageClick(totalPages)} disabled={isLoading}>{totalPages}</button>}
                            </>
                        )}
                        <button onClick={handleNextPage} disabled={endIndex >= totalCount || isLoading}>다음</button>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default CampaignHistory;

